'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { api } from '@/lib/apiClient';
import { useRouter } from 'next/navigation';
import Cookies from 'js-cookie';

const userRoles = [
    { value: 'buyer', label: 'Buyer / Tenant', description: 'Looking to buy or rent property' },
    { value: 'seller', label: 'Seller / Broker', description: 'Want to sell or list property' },
];

export default function RegisterPage() {
    const router = useRouter();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        role: 'buyer',
        agreeTerms: false,
    });

    const [otp, setOtp] = useState(['', '', '', '']);
    const [showOtp, setShowOtp] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [countdown, setCountdown] = useState(0);
    const [otpId, setOtpId] = useState('');

    const nameRef = useRef(null);
    const phoneRef = useRef(null);
    const emailRef = useRef(null);

    const otpRefs = useRef([]);
    const timerRef = useRef(null);

    // Cleanup timer
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []);

    // Start countdown
    const startCountdown = () => {
        setCountdown(60);
        timerRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        setError('');
        setFieldErrors(prev => ({ ...prev, [name]: false }));
    };

    // OTP Change
    const handleOtpChange = (value, index) => {
        if (!/^\d?$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        setError('');

        // Move to next input
        if (value && index < 3) {
            otpRefs.current[index + 1]?.focus();
        }

        // Auto-verify when OTP is complete
        if (newOtp.join('').length === 4) {
            //    handleVerifyOtp(newOtp.join(''));
        }
    };

    // Handle backspace
    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    // Get OTP
    const handleGetOtp = async () => {
        // Validation
        const newErrors = {};
        let firstFocusRef = null;

        if (!formData.name?.trim()) {
            newErrors.name = true;
            firstFocusRef = firstFocusRef || nameRef;
        }
        
        if (!formData.phone || formData.phone.length !== 10) {
            newErrors.phone = true;
            firstFocusRef = firstFocusRef || phoneRef;
        }

        if (!formData.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = true;
            firstFocusRef = firstFocusRef || emailRef;
        }

        if (Object.keys(newErrors).length > 0) {
            setFieldErrors(newErrors);
            if (firstFocusRef && firstFocusRef.current) {
                firstFocusRef.current.focus();
            }
            return;
        }

        setError('');
        setFieldErrors({});
        setIsLoading(true);

        try {
            const response = await api.post('getotp', {
                mobile: formData.phone,
                name: formData.name,
                email: formData.email
            });

            if (response.success === 1) {
                console.log("OTP sent to mobile" + response.otp); // Don't log OTP in production

                setOtpId(response.otp || '');
                setShowOtp(true);
                setOtp(['', '', '', '']);
                startCountdown();

                // Focus first OTP box
                setTimeout(() => {
                    otpRefs.current[0]?.focus();
                }, 100);

            } else {
                setError(response.msg || 'Failed to send OTP');
            }
        } catch (error) {
            console.error(error);
            setError('Network error. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Verify OTP
    const handleVerifyOtp = async (enteredOtp) => {
        setIsLoading(true);

        try {
            const response = await api.post('verify-register-otp', {
                mobile: formData.phone,
                otp: enteredOtp,
                otp_id: otpId
            });

            if (response.success === 1) {
                // OTP verified successfully
                setError('');
                // Continue with registration
            } else {
                setError(response.msg || 'Invalid OTP');
                setOtp(['', '', '', '']); // Clear OTP
                otpRefs.current[0]?.focus(); // Focus first box
            }
        } catch (error) {
            console.log(error);
            setError('Verification failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Handle final registration
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Check if OTP is entered
        if (otp.join('').length !== 4) {
            setError('Please enter OTP');
            return;
        }

        if (!formData.agreeTerms) {
            setError('Please agree to Terms and Conditions');
            return;
        }

        setIsLoading(true);

        try {
            const response = await api.post('insertwithout/users', {
                mobile: formData.phone,
                full_name: formData.name,
                email: formData.email,
                user_role: formData.role,
                is_verified: 1,
                mobile_verified: 1,
                //  otp_id: otpId // Include OTP ID for verification
            });

            if (response.success === 1) {
                // Auto-login after registration
                var insid = response.insId;
                const response2 = await api.post('checklogin', { mobile: formData.phone });

                if (response2.success === 1) {
                    const expiresIn = response2.expiresIn || 3600;

                    const cookieOptions = {
                        secure: process.env.NODE_ENV === 'production',
                        sameSite: 'strict',
                        expires: new Date(Date.now() + expiresIn * 1000),
                        path: '/',
                    };

                    Cookies.set('token', response2.token, cookieOptions);
                    Cookies.set('name', JSON.stringify(response2.name), cookieOptions);
                    Cookies.set('id', JSON.stringify(response2.id), cookieOptions);
                    Cookies.set('mobile', JSON.stringify(response2.mobile), cookieOptions);
                    Cookies.set('role', JSON.stringify(response2.userrole), cookieOptions);

                    if (['seller', 'agent'].includes(response2.userrole)) {
                        router.push('/seller/post-property');
                    } else if (['builder'].includes(response2.userrole)) {
                        router.push('/seller/projects');
                    } else {
                        router.push('/my/activity');
                    }
                }
            } else {
                setError(response.msg || 'Registration failed');
            }
        } catch (error) {
            console.error(error);
            setError('Registration failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="auth-page">
            <div className="auth-card max-w-lg">
                <div className="auth-header">
                    <Link href="/" className="logo inline-flex items-center gap-2">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-light flex items-center justify-center">
                            <span className="text-white font-bold">DA</span>
                        </div>
                        <span className="text-xl font-bold">
                            Dream<span className="text-primary">Acres</span>
                        </span>
                    </Link>
                    <h1>Create Account</h1>
                    <p>Join DreamAcres to explore properties</p>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="auth-form">
                    {/* Role Selection */}
                    <div className="form-group">
                        <label>I am a</label>
                        <div className="grid grid-cols-2 gap-3">
                            {userRoles.map((role) => {
                                const isSelected = role.value === 'buyer'
                                    ? formData.role === 'buyer'
                                    : ['seller', 'agent', 'builder'].includes(formData.role);

                                return (
                                    <label
                                        key={role.value}
                                        className={`relative flex flex-col p-4 border-2 rounded-xl cursor-pointer transition-all ${isSelected
                                            ? 'border-primary bg-primary/5'
                                            : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        <input
                                            type="radio"
                                            name="role"
                                            value={role.value}
                                            checked={isSelected}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setFormData(prev => ({ ...prev, role: val }));
                                            }}
                                            className="sr-only"
                                            disabled={showOtp}
                                        />
                                        <span className="font-medium">{role.label}</span>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {role.description}
                                        </p>
                                    </label>
                                );
                            })}
                        </div>

                        {/* Sub-role Selection for Seller/Broker */}
                        {['seller', 'agent', 'builder'].includes(formData.role) && (
                            <div className="mt-3 animate-fadeIn">
                                <label>Select your role</label>
                                <select
                                    name="role"
                                    value={['seller', 'agent', 'builder'].includes(formData.role) ? formData.role : 'seller'}
                                    onChange={handleChange}
                                    className="input w-full"
                                    disabled={showOtp}
                                >
                                    <option value="seller">Individual Seller</option>
                                    <option value="agent">Agent / Broker</option>
                                    <option value="builder">Builder / Developer</option>
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Name + Phone */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="form-group">
                            <label>Full Name</label>
                            <input
                                type="text"
                                name="name"
                                ref={nameRef}
                                value={formData.name}
                                onChange={handleChange}
                                className={`input ${fieldErrors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-100' : ''}`}
                                placeholder="Enter your name"
                                disabled={showOtp}
                            />
                        </div>

                        <div className="form-group">
                            <label>Mobile Number</label>
                            <input
                                type="tel"
                                name="phone"
                                ref={phoneRef}
                                value={formData.phone}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                                    setFormData(prev => ({ ...prev, phone: value }));
                                    setFieldErrors(prev => ({ ...prev, phone: false }));
                                    setError('');
                                }}
                                className={`input ${fieldErrors.phone ? 'border-red-500 focus:border-red-500 focus:ring-red-100' : ''}`}
                                placeholder="10-digit mobile"
                                maxLength={10}
                                disabled={showOtp}
                            />
                        </div>
                    </div>

                    {/* Email + Get OTP */}
                    <div className="form-group">
                        <label>Email Address</label>
                        <div className="flex gap-2">
                            <input
                                type="email"
                                name="email"
                                ref={emailRef}
                                value={formData.email}
                                onChange={handleChange}
                                className={`input flex-1 ${fieldErrors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-100' : ''}`}
                                placeholder="Enter your email"
                                disabled={showOtp}
                            />
                            {!showOtp ? (
                                <button
                                    type="button"
                                    onClick={handleGetOtp}
                                    className="btn btn-primary whitespace-nowrap"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Sending...' : 'Get OTP'}
                                </button>
                            ) : countdown > 0 ? (
                                <div className="px-4 py-2 bg-gray-200 rounded-md text-sm font-medium min-w-[80px] text-center">
                                    00:{countdown.toString().padStart(2, '0')}
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleGetOtp}
                                    className="btn btn-primary whitespace-nowrap"
                                    disabled={isLoading}
                                >
                                    Resend OTP
                                </button>
                            )}
                        </div>
                    </div>

                    {/* OTP Input Section */}
                    {showOtp && (
                        <div className="form-group mt-4">
                            <label>Enter OTP</label>
                            <p className="text-sm text-gray-500 mb-2">
                                OTP sent to {formData.phone}
                            </p>
                            <div className="flex gap-3 justify-center mt-2">
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength="1"
                                        value={digit}
                                        ref={el => otpRefs.current[index] = el}
                                        onChange={(e) => handleOtpChange(e.target.value, index)}
                                        // onKeyDown={(e) => handleKeyDown(e, index)}
                                        className="w-14 h-14 text-center border-2 rounded-xl text-xl font-semibold focus:border-primary focus:outline-none transition-colors"
                                        disabled={isLoading}
                                    />
                                ))}
                            </div>

                            {/* Terms & Conditions - Show only after OTP verification */}
                            {otp.join('').length === 4 && !error && (
                                <>
                                    <label className="flex items-start gap-3 cursor-pointer mt-6">
                                        <input
                                            type="checkbox"
                                            name="agreeTerms"
                                            checked={formData.agreeTerms}
                                            onChange={handleChange}
                                            className="w-4 h-4 mt-1"
                                        />
                                        <span className="text-sm text-gray-600">
                                            I agree to the{' '}
                                            <a href="/terms" className="text-primary hover:underline">
                                                Terms of Service
                                            </a>{' '}
                                            and{' '}
                                            <a href="/privacy" className="text-primary hover:underline">
                                                Privacy Policy
                                            </a>
                                        </span>
                                    </label>

                                    <button
                                        type="submit"
                                        disabled={isLoading || !formData.agreeTerms}
                                        className="btn btn-primary w-full mt-4"
                                    >
                                        {isLoading ? (
                                            <span className="flex items-center justify-center gap-2">
                                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                </svg>
                                                Creating Account...
                                            </span>
                                        ) : 'Create Account'}
                                    </button>
                                </>
                            )}

                            {/* Resend hint */}
                            {countdown === 0 && (
                                <p className="text-center text-sm text-gray-500 mt-4">
                                    Didn't receive OTP?{' '}
                                    <button
                                        type="button"
                                        onClick={handleGetOtp}
                                        className="text-primary font-medium hover:underline"
                                        disabled={isLoading}
                                    >
                                        Resend OTP
                                    </button>
                                </p>
                            )}
                        </div>
                    )}
                </form>

                <p className="text-center text-gray-600 text-sm mt-6">
                    Already have an account?{' '}
                    <Link href="/login" className="text-primary font-medium hover:underline">
                        Login
                    </Link>
                </p>
            </div>
        </div>
    );
}